'use client';

import { useState } from 'react';

interface GoogleLoginProps {
    onSuccess?: (user: { email: string; name: string; picture: string }) => void;
    onError?: (error: string) => void;
}

export default function GoogleLogin({ onError }: GoogleLoginProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleGoogleLogin = () => {
        setIsLoading(true);
        setError(null);

        try {
            // Generate a random state parameter for security
            const state = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
            
            // Store state in sessionStorage for verification
            sessionStorage.setItem('google_oauth_state', state);
            
            // Build the Google OAuth URL
            const googleAuthUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
            googleAuthUrl.searchParams.set('client_id', process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || '');
            googleAuthUrl.searchParams.set('redirect_uri', `${window.location.origin}/api/auth/google/callback`);
            googleAuthUrl.searchParams.set('response_type', 'code');
            googleAuthUrl.searchParams.set('scope', 'openid email profile');
            googleAuthUrl.searchParams.set('state', state);
            googleAuthUrl.searchParams.set('access_type', 'offline');
            googleAuthUrl.searchParams.set('prompt', 'select_account');

            // Redirect to Google OAuth
            window.location.href = googleAuthUrl.toString();
            
        } catch (err: unknown) {
            const errorMessage = (err as Error)?.message || 'Failed to initiate Google login';
            setError(errorMessage);
            setIsLoading(false);
            if (onError) {
                onError(errorMessage);
            }
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-black py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
                        Analytics Dashboard Access
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-300">
                        Sign in with your Google account to view analytics
                    </p>
                </div>

                <div className="mt-8 space-y-6">
                    {error && (
                        <div className="bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded-md">
                            {error}
                        </div>
                    )}

                    <div className="flex justify-center">
                        <button
                            onClick={handleGoogleLogin}
                            disabled={isLoading}
                            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            {isLoading ? (
                                <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    Signing in...
                                </div>
                            ) : (
                                <div className="flex items-center">
                                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                        <path
                                            fill="currentColor"
                                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                        />
                                        <path
                                            fill="currentColor"
                                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                        />
                                        <path
                                            fill="currentColor"
                                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                        />
                                        <path
                                            fill="currentColor"
                                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                        />
                                    </svg>
                                    Sign in with Google
                                </div>
                            )}
                        </button>
                    </div>

                    <div className="text-center text-sm text-gray-400">
                        <p>Only authorized users can access the analytics dashboard.</p>
                        <p className="mt-1">If you believe this is an error, please contact the administrator.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}